import React from "react";
import api from "../api/client";
import { useOrg } from "../context/OrgContext";
import { useAuth } from "../context/AuthContext";
import { useTerminology } from "../context/TerminologyContext";
import PageHeader from "./PageHeader";
import PinModal, { usePinGate } from "./PinModal";
import { Pager } from "./ui-helpers";
import { formatDateTimeFull } from "../utils/datetime";

const STATUSES = [
  "Submitted",
  "Initiated",
  "Queued",
  "On Hold",
  "Pending",
  "Approved",
  "Rejected",
  "Invalidated",
  "SentBack",
  "Withdrawn",
];
const GROUPS = ["Viewer", "User", "Vendor", "Admin", "Manager", "Super Admin"];

const STATUS_DISPLAY_LABELS = {
  SentBack: "Revert Material",
  Withdrawn: "Withdrawn Material",
};

function statusDisplayLabel(statusKey) {
  return STATUS_DISPLAY_LABELS[statusKey] || statusKey;
}

const EMPTY_EMAIL = {
  email_enabled: false,
  login_otp_enabled: true,
  email_transport: "smtp",
  smtp_host: "",
  smtp_port: 25,
  smtp_user: "",
  smtp_password: "",
  smtp_use_tls: false,
  from_name: "QualiGate",
  from_email: "",
  zeptomail_api_url: "https://api.zeptomail.in/v1.1/email",
  zeptomail_api_key: "",
  zeptomail_api_key_configured: false,
  zeptomail_timeout_seconds: 30,
};

const EMPTY_SUPER_ADMIN_SECURITY = {
  email: "",
  email_verified: false,
  email_verified_at: null,
  login_otp_enabled: true,
  env_override: null,
  effective_login_otp_enabled: true,
};

const EMPTY_QUEUE_POLICY = {
  email_queue_enabled: true,
  email_max_retries: 3,
  email_retry_intervals: "60,300,900",
  email_job_timeout_seconds: 60,
  email_failure_ttl_hours: 24,
};

const EMAIL_JOB_STATUS_OPTIONS = [
  "queued",
  "started",
  "sent",
  "partial",
  "failed",
  "dead",
  "disabled",
  "enqueue_failed",
];

const TERMINOLOGY_FIELDS = [
  { singularKey: "vendor", pluralKey: "vendor_plural", label: "Vendor" },
  { singularKey: "material", pluralKey: "material_plural", label: "Material" },
  { singularKey: "lot", pluralKey: "lot_plural", label: "Lot Number" },
  { singularKey: "type", pluralKey: "type_plural", label: "Type" },
];

function RecipientsEditor({
  title,
  values = [],
  onToggle,
  includeSuperAdmin = false,
}) {
  const visibleGroups = includeSuperAdmin
    ? GROUPS
    : GROUPS.filter((group) => group !== "Super Admin");
  return (
    <div className="field span-6">
      <label>{title}</label>
      <div className="checklist-grid">
        {visibleGroups.map((group) => (
          <label key={group} className="checkbox-row">
            <input
              type="checkbox"
              checked={values.includes(group)}
              onChange={() => onToggle(group)}
            />
            <span>{group}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function StatusEditor({
  statusKey,
  row,
  setField,
  toggleGroup,
  includeSuperAdminRecipient = false,
}) {
  return (
    <div className="section notify-template-card notify-status-shell">
      <div className="page-header-row">
        <div>
          <h3>{statusDisplayLabel(statusKey)}</h3>
          <div className="helper-text">
            Keep the subject, in-app message, and email body aligned for this
            workflow outcome.
          </div>
        </div>
      </div>
      <div className="form-grid notify-grid notify-grid--wide">
        <div className="field span-8">
          <label>
            Subject
            <input
              value={row.subject || ""}
              onChange={(e) => setField(statusKey, "subject", e.target.value)}
            />
          </label>
        </div>
        <div className="field span-4">
          <label>
            Notification message
            <input
              value={row.notification || ""}
              onChange={(e) =>
                setField(statusKey, "notification", e.target.value)
              }
            />
          </label>
        </div>
        <div className="field span-12">
          <label>
            Email body
            <textarea
              rows={9}
              value={row.body || ""}
              onChange={(e) => setField(statusKey, "body", e.target.value)}
            />
          </label>
        </div>
        <RecipientsEditor
          title="Send to"
          values={row.to || []}
          onToggle={(role) => toggleGroup(statusKey, "to", role)}
          includeSuperAdmin={includeSuperAdminRecipient}
        />
        <RecipientsEditor
          title="CC"
          values={row.cc || []}
          onToggle={(role) => toggleGroup(statusKey, "cc", role)}
          includeSuperAdmin={includeSuperAdminRecipient}
        />
      </div>
    </div>
  );
}

export default function NotifyConfigWorkspace({
  title,
  subtitle,
  eyebrow,
  icon = "notifications",
  allowEmailTab = false,
  statusTabbed = false,
}) {
  const { current: org } = useOrg();
  const { user } = useAuth();
  const pinGate = usePinGate();
  const { labels, defaults, saveTerminology, reloadTerminology } =
    useTerminology();
  const [activeTab, setActiveTab] = React.useState("notifications");
  const [activeStatus, setActiveStatus] = React.useState(STATUSES[0]);
  const [cfg, setCfg] = React.useState({});
  const [emailCfg, setEmailCfg] = React.useState(EMPTY_EMAIL);
  const [superAdminSecurity, setSuperAdminSecurity] = React.useState(
    EMPTY_SUPER_ADMIN_SECURITY,
  );
  const [queuePolicy, setQueuePolicy] = React.useState(EMPTY_QUEUE_POLICY);
  const [queueHealth, setQueueHealth] = React.useState(null);
  const [queueJobs, setQueueJobs] = React.useState([]);
  const [queueJobsPage, setQueueJobsPage] = React.useState(1);
  const [queueJobsPageSize, setQueueJobsPageSize] = React.useState(25);
  const [queueJobsTotal, setQueueJobsTotal] = React.useState(0);
  const [queueJobsStatus, setQueueJobsStatus] = React.useState("");
  const [terminologyDraft, setTerminologyDraft] = React.useState(defaults);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");

  const fetchQueueJobs = React.useCallback(async (page, status, pageSize) => {
    if (!allowEmailTab) {
      setQueueJobs([]);
      setQueueJobsTotal(0);
      return;
    }
    const requestedPage = Math.max(1, Number(page) || 1);
    const requestedSize = Math.max(1, Number(pageSize) || 25);
    try {
      const res = await api.get("/superadmin/email-queue/jobs", {
        params: {
          page: requestedPage,
          page_size: requestedSize,
          status: status || undefined,
        },
      });
      const total = Number(res.data?.total || 0);
      const pageCount = Math.max(1, Math.ceil(total / requestedSize));
      setQueueJobsTotal(total);
      if (requestedPage > pageCount) {
        setQueueJobs([]);
        setQueueJobsPage(pageCount);
        return;
      }
      setQueueJobs(res.data?.jobs || []);
      setQueueJobsPage(Number(res.data?.page || requestedPage));
    } catch (_) {
      setQueueJobs([]);
      setQueueJobsTotal(0);
    }
  }, [allowEmailTab]);

  const load = React.useCallback(async () => {
    setError("");
    const [
      notifyRes,
      emailRes,
      superAdminSecurityRes,
      queuePolicyRes,
      queueHealthRes,
    ] =
      await Promise.allSettled([
        api.get("/notify/config"),
        allowEmailTab
          ? api.get("/notify/email_config")
          : Promise.resolve({ data: EMPTY_EMAIL }),
        allowEmailTab
          ? api.get("/superadmin/security-settings")
          : Promise.resolve({ data: EMPTY_SUPER_ADMIN_SECURITY }),
        allowEmailTab
          ? api.get("/superadmin/email-queue/policy")
          : Promise.resolve({ data: { policy: EMPTY_QUEUE_POLICY } }),
        allowEmailTab
          ? api.get("/superadmin/email-queue/health")
          : Promise.resolve({ data: null }),
      ]);

    if (notifyRes.status === "fulfilled") {
      setCfg(notifyRes.value.data || {});
    } else {
      setCfg({});
      setError("Unable to load notification settings.");
    }

    if (allowEmailTab && emailRes.status === "fulfilled") {
      setEmailCfg({ ...EMPTY_EMAIL, ...(emailRes.value.data || {}) });
    }
    if (allowEmailTab && superAdminSecurityRes.status === "fulfilled") {
      setSuperAdminSecurity({
        ...EMPTY_SUPER_ADMIN_SECURITY,
        ...(superAdminSecurityRes.value.data || {}),
      });
    } else if (allowEmailTab) {
      setError("Unable to load global Super Admin identity/security settings.");
    }
    if (allowEmailTab && queuePolicyRes.status === "fulfilled") {
      setQueuePolicy({
        ...EMPTY_QUEUE_POLICY,
        ...(queuePolicyRes.value.data?.policy || {}),
      });
    }
    if (allowEmailTab && queueHealthRes.status === "fulfilled") {
      setQueueHealth(queueHealthRes.value.data || null);
    }
  }, [allowEmailTab]);

  React.useEffect(() => {
    load().catch(() => setError("Unable to load configuration."));
  }, [load, org]);

  React.useEffect(() => {
    fetchQueueJobs(queueJobsPage, queueJobsStatus, queueJobsPageSize);
  }, [fetchQueueJobs, queueJobsPage, queueJobsPageSize, queueJobsStatus, org]);

  React.useEffect(() => {
    setTerminologyDraft({ ...defaults, ...(labels || {}) });
  }, [defaults, labels]);

  const setField = (statusKey, field, value) => {
    setCfg((current) => ({
      ...current,
      [statusKey]: { ...(current[statusKey] || {}), [field]: value },
    }));
  };

  const toggleGroup = (statusKey, field, role) => {
    setCfg((current) => {
      const next = new Set(current[statusKey]?.[field] || []);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return {
        ...current,
        [statusKey]: {
          ...(current[statusKey] || {}),
          [field]: Array.from(next),
        },
      };
    });
  };

  const performSaveNotify = async (payload) => {
    try {
      await api.post("/notify/config", payload);
      setMessage("Notification configuration saved.");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Unable to save notification configuration.",
      );
    }
  };

  const saveNotify = () => {
    setMessage("");
    setError("");
    pinGate.askPinThen(() => performSaveNotify(cfg));
  };

  const saveEmail = async () => {
    setMessage("");
    setError("");
    try {
      const response = await api.put("/notify/email_config", {
        ...emailCfg,
        smtp_port: parseInt(emailCfg.smtp_port || 25, 10),
        zeptomail_timeout_seconds:
          emailCfg.zeptomail_timeout_seconds ?? 30,
      });
      setEmailCfg({ ...EMPTY_EMAIL, ...(response.data || {}) });
      setMessage("Email configuration saved.");
    } catch (err) {
      setError(
        err?.response?.data?.error || "Unable to save email configuration.",
      );
    }
  };

  const saveSuperAdminSecurity = async () => {
    setMessage("");
    setError("");
    try {
      const form = new FormData();
      form.append("email", String(superAdminSecurity.email || "").trim());
      form.append(
        "login_otp_enabled",
        superAdminSecurity.login_otp_enabled ? "true" : "false",
      );
      const response = await api.post(
        "/superadmin/security-settings",
        form,
      );
      setSuperAdminSecurity({
        ...EMPTY_SUPER_ADMIN_SECURITY,
        ...(response.data || {}),
      });
      setMessage(response.data?.env_override_active
        ? "Settings saved. The managed email is the fallback; environment override still controls delivery."
        : "Global Super Admin identity/security settings saved.");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Unable to save global Super Admin identity/security settings.",
      );
    }
  };

  const saveQueuePolicy = async () => {
    setMessage("");
    setError("");
    try {
      const response = await api.post("/superadmin/email-queue/policy", {
        email_queue_enabled: !!queuePolicy.email_queue_enabled,
        email_max_retries: Number(queuePolicy.email_max_retries),
        email_retry_intervals: String(queuePolicy.email_retry_intervals || ""),
        email_job_timeout_seconds: Number(
          queuePolicy.email_job_timeout_seconds,
        ),
        email_failure_ttl_hours: Number(queuePolicy.email_failure_ttl_hours),
      });
      setQueuePolicy({
        ...EMPTY_QUEUE_POLICY,
        ...(response.data?.policy || {}),
      });
      const health = await api.get("/superadmin/email-queue/health").catch(() => null);
      if (health?.data) setQueueHealth(health.data);
      await fetchQueueJobs(queueJobsPage, queueJobsStatus, queueJobsPageSize);
      setMessage("Email queue settings saved.");
    } catch (err) {
      setError(
        err?.response?.data?.error || "Unable to save email queue settings.",
      );
    }
  };

  const saveTerminologyConfig = async () => {
    setMessage("");
    setError("");
    try {
      await saveTerminology(terminologyDraft);
      await reloadTerminology();
      setMessage("Terminology configuration saved.");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Unable to save terminology configuration.",
      );
    }
  };

  const draftLabel = React.useCallback(
    (key) => {
      const value = terminologyDraft?.[key];
      return typeof value === "string" && value.trim()
        ? value.trim()
        : defaults[key] || key;
    },
    [terminologyDraft, defaults],
  );
  const draftPlural = React.useCallback(
    (key) => {
      const pluralKey = `${key}_plural`;
      const value = terminologyDraft?.[pluralKey];
      return typeof value === "string" && value.trim()
        ? value.trim()
        : defaults[pluralKey] || `${draftLabel(key)}s`;
    },
    [terminologyDraft, defaults, draftLabel],
  );
  const superAdminOtpOverrideActive =
    superAdminSecurity.env_override !== null &&
    superAdminSecurity.env_override !== undefined;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow={eyebrow}
        icon={icon}
        title={title}
        subtitle={subtitle}
      />

      <div className="tabs">
        <button
          className={activeTab === "notifications" ? "chip chip-on" : "chip"}
          type="button"
          onClick={() => setActiveTab("notifications")}
        >
          Configure Notifications
        </button>
        {allowEmailTab ? (
          <>
            <button
              className={activeTab === "emails" ? "chip chip-on" : "chip"}
              type="button"
              onClick={() => setActiveTab("emails")}
            >
              Configure Emails
            </button>
            <button
              className={activeTab === "terminology" ? "chip chip-on" : "chip"}
              type="button"
              onClick={() => setActiveTab("terminology")}
            >
              Terminology
            </button>
          </>
        ) : null}
      </div>

      {message ? <div className="banner success">{message}</div> : null}
      {error ? <div className="banner error">{error}</div> : null}

      {activeTab === "notifications" ? (
        <div className="page-stack">
          <div className="section">
            <div className="helper-text">
              {allowEmailTab ? "Current organization" : "Workspace"}:{" "}
              <strong>{org || "default"}</strong>. In-app notifications remain
              primary, with templates mapped by lot status.
            </div>
            <div className="helper-text" style={{ marginTop: 8 }}>
              Available variables include: {"{$Vendor}"}, {"{$Material}"},{" "}
              {"{$SKU}"}, {"{$Lot No}"}, {"{$STD_MRP}"}, {"{$Mfg Date}"},{" "}
              {"{$EXP_DATE}"}, {"{$Date & Time}"}, {"{$Comments}"}, {"{$Reason}"},{" "}
              {"{$Reviewed By}"}, and {"{$Review Date}"}.
            </div>
          </div>

          {statusTabbed ? (
            <>
              <div className="tabs notify-status-tabs">
                {STATUSES.map((statusKey) => (
                  <button
                    key={statusKey}
                    className={
                      activeStatus === statusKey ? "chip chip-on" : "chip"
                    }
                    type="button"
                    onClick={() => setActiveStatus(statusKey)}
                  >
                    {statusKey}
                  </button>
                ))}
              </div>
              <StatusEditor
                key={activeStatus}
                statusKey={activeStatus}
                row={cfg[activeStatus] || {}}
                setField={setField}
                toggleGroup={toggleGroup}
                includeSuperAdminRecipient={allowEmailTab}
              />
            </>
          ) : (
            STATUSES.map((statusKey) => (
              <StatusEditor
                key={statusKey}
                statusKey={statusKey}
                row={cfg[statusKey] || {}}
                setField={setField}
                toggleGroup={toggleGroup}
                includeSuperAdminRecipient={allowEmailTab}
              />
            ))
          )}

          <div className="card-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={saveNotify}
            >
              Save notifications
            </button>
          </div>
        </div>
      ) : null}

      {allowEmailTab && activeTab === "terminology" ? (
        <div className="section notify-template-card">
          <div className="page-header-row">
            <div>
              <h3>Terminology</h3>
              <div className="helper-text">
                Current organization: <strong>{org || "default"}</strong>. These
                labels change display text only; role names, routes, and data
                fields stay unchanged.
              </div>
            </div>
          </div>
          <div className="form-grid email-config-grid">
            {TERMINOLOGY_FIELDS.map((field) => (
              <React.Fragment key={field.singularKey}>
                <div className="field span-6">
                  <label>
                    {field.label} singular
                    <input
                      value={terminologyDraft[field.singularKey] ?? ""}
                      placeholder={defaults[field.singularKey]}
                      maxLength={60}
                      onChange={(e) =>
                        setTerminologyDraft((current) => ({
                          ...current,
                          [field.singularKey]: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="field span-6">
                  <label>
                    {field.label} plural
                    <input
                      value={terminologyDraft[field.pluralKey] ?? ""}
                      placeholder={defaults[field.pluralKey]}
                      maxLength={60}
                      onChange={(e) =>
                        setTerminologyDraft((current) => ({
                          ...current,
                          [field.pluralKey]: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="helper-text">
            Preview: Vendor → {draftLabel("vendor")} / {draftPlural("vendor")} |
            Material → {draftLabel("material")} / {draftPlural("material")} |
            Lot Number → {draftLabel("lot")} / {draftPlural("lot")} | Type →{" "}
            {draftLabel("type")} / {draftPlural("type")}
          </div>
          <div className="card-actions">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setTerminologyDraft({ ...defaults })}
            >
              Reset to defaults
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={saveTerminologyConfig}
            >
              Save terminology
            </button>
          </div>
        </div>
      ) : null}

      {allowEmailTab && activeTab === "emails" ? (
        <div className="page-stack">
          <div className="section notify-template-card">
            <div className="page-header-row">
              <div>
                <h3>Global Super Admin login identity &amp; security</h3>
                <div className="helper-text">
                  These settings control the global Super Admin sign-in identity.
                  They are separate from the selected organization&apos;s outbound
                  email delivery configuration below.
                </div>
              </div>
              <span
                className={`status-pill ${
                  superAdminSecurity.email_verified
                    ? "status-approved"
                    : "status-pending"
                }`}
              >
                {superAdminSecurity.email_verified ? "Verified" : "Unverified"}
              </span>
            </div>
            <div className="helper-text" role="status">
              Effective operational email: <strong>{superAdminSecurity.effective_email || "Not configured"}</strong>.
              {superAdminSecurity.env_override_active
                ? " Environment override active (SUPER_ADMIN_EMAIL). Delivery uses this trusted deployment address. The managed email remains editable as a fallback and controls delivery when the override is removed."
                : " Delivery uses the managed email."}
            </div>
            <div className="form-grid email-config-grid">
              <div className="field span-6">
                <label>
                  Managed Super Admin email (stored fallback)
                  <input
                    type="email"
                    autoComplete="email"
                    value={superAdminSecurity.email || ""}
                    onChange={(event) =>
                      setSuperAdminSecurity((current) => ({
                        ...current,
                        email: event.target.value,
                      }))
                    }
                  />
                </label>
                <div className="helper-text">
                  Changing the address marks it unverified and invalidates stale
                  sign-in or recovery challenges for the previous address.
                </div>
              </div>
              <div className="field span-6">
                <label>Stored email verification state</label>
                <div className="helper-text">
                  <strong>
                    {superAdminSecurity.email_verified
                      ? "Verified"
                      : "Unverified"}
                  </strong>
                  {superAdminSecurity.email_verified_at ? (
                    <> · {formatDateTimeFull(superAdminSecurity.email_verified_at)}</>
                  ) : null}
                </div>
              </div>
              <label className="field span-6 checkbox-row">
                <input
                  type="checkbox"
                  checked={!!superAdminSecurity.login_otp_enabled}
                  disabled={superAdminOtpOverrideActive}
                  onChange={(event) =>
                    setSuperAdminSecurity((current) => ({
                      ...current,
                      login_otp_enabled: event.target.checked,
                    }))
                  }
                />
                <span>Stored recurring login OTP preference</span>
              </label>
              <div className="field span-6">
                <label>Effective recurring login OTP</label>
                <div className="helper-text">
                  <strong>
                    {superAdminSecurity.effective_login_otp_enabled
                      ? "Enabled"
                      : "Disabled"}
                  </strong>
                  {superAdminOtpOverrideActive
                    ? " by environment override"
                    : " from the stored preference"}
                </div>
              </div>
            </div>
            {superAdminOtpOverrideActive ? (
              <div className="banner warn">
                Environment override is active and currently forces recurring
                login OTP {superAdminSecurity.env_override ? "on" : "off"}.
                The stored preference is shown for reference and does not control
                the effective state until the override is removed.
              </div>
            ) : null}
            <div className="helper-text">
              Email verification and recurring login OTP are separate controls.
              A newly changed, unverified address may require a one-time email
              verification even when recurring login OTP is disabled.
            </div>
            <div className="card-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={saveSuperAdminSecurity}
              >
                Save global identity/security
              </button>
            </div>
          </div>

          <div className="section notify-template-card">
          <div className="page-header-row">
            <div>
              <h3>Org email delivery</h3>
              <div className="helper-text">
                Current organization: <strong>{org || "default"}</strong>. Choose
                how this organization sends queued email.
              </div>
            </div>
          </div>
          <div className="form-grid email-config-grid">
            <label className="field span-12 checkbox-row">
              <input
                type="checkbox"
                checked={!!emailCfg.email_enabled}
                onChange={(e) =>
                  setEmailCfg((current) => ({
                    ...current,
                    email_enabled: e.target.checked,
                  }))
                }
              />
              <span>
                Enable org email delivery when an org email configuration exists
              </span>
            </label>
            <label className="field span-12 checkbox-row">
              <input
                type="checkbox"
                checked={emailCfg.login_otp_enabled !== false}
                onChange={(e) =>
                  setEmailCfg((current) => ({
                    ...current,
                    login_otp_enabled: e.target.checked,
                  }))
                }
              />
              <span>Require OTP for verified-user login</span>
            </label>
            <div className="field span-12">
              <label>
                Delivery method
                <select
                  value={emailCfg.email_transport || "smtp"}
                  onChange={(e) =>
                    setEmailCfg((current) => ({
                      ...current,
                      email_transport: e.target.value,
                    }))
                  }
                >
                  <option value="smtp">SMTP</option>
                  <option value="zeptomail_api">ZeptoMail API</option>
                </select>
              </label>
            </div>
            {(emailCfg.email_transport || "smtp") === "smtp" ? (
              <>
                <div className="field span-8">
                  <label>
                    SMTP host
                    <input
                      value={emailCfg.smtp_host || ""}
                      onChange={(e) =>
                        setEmailCfg((current) => ({
                          ...current,
                          smtp_host: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="field span-4">
                  <label>
                    SMTP port
                    <input
                      type="number"
                      min="1"
                      value={emailCfg.smtp_port || 25}
                      onChange={(e) =>
                        setEmailCfg((current) => ({
                          ...current,
                          smtp_port: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="field span-6">
                  <label>
                    SMTP username
                    <input
                      value={emailCfg.smtp_user || ""}
                      onChange={(e) =>
                        setEmailCfg((current) => ({
                          ...current,
                          smtp_user: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="field span-6">
                  <label>
                    SMTP password
                    <input
                      type="password"
                      value={emailCfg.smtp_password || ""}
                      onChange={(e) =>
                        setEmailCfg((current) => ({
                          ...current,
                          smtp_password: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <label className="field span-12 checkbox-row">
                  <input
                    type="checkbox"
                    checked={!!emailCfg.smtp_use_tls}
                    onChange={(e) =>
                      setEmailCfg((current) => ({
                        ...current,
                        smtp_use_tls: e.target.checked,
                      }))
                    }
                  />
                  <span>Use TLS</span>
                </label>
              </>
            ) : (
              <>
                <div className="field span-8">
                  <label>
                    ZeptoMail API endpoint
                    <input
                      value={emailCfg.zeptomail_api_url || ""}
                      onChange={(e) =>
                        setEmailCfg((current) => ({
                          ...current,
                          zeptomail_api_url: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="field span-4">
                  <label>
                    Timeout (seconds)
                    <input
                      type="number"
                      min="1"
                      max="120"
                      value={emailCfg.zeptomail_timeout_seconds || 30}
                      onChange={(e) =>
                        setEmailCfg((current) => ({
                          ...current,
                          zeptomail_timeout_seconds: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="field span-12">
                  <label>
                    ZeptoMail API token
                    <input
                      type="password"
                      value={emailCfg.zeptomail_api_key || ""}
                      autoComplete="new-password"
                      onChange={(e) =>
                        setEmailCfg((current) => ({
                          ...current,
                          zeptomail_api_key: e.target.value,
                        }))
                      }
                    />
                  </label>
                  {emailCfg.zeptomail_api_key_configured ? (
                    <div className="helper-text">
                      An API token is configured. Leave this field blank to preserve it.
                    </div>
                  ) : null}
                </div>
              </>
            )}
            <div className="field span-6">
              <label>
                From name
                <input
                  value={emailCfg.from_name || ""}
                  onChange={(e) =>
                    setEmailCfg((current) => ({
                      ...current,
                      from_name: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field span-6">
              <label>
                From email
                <input
                  value={emailCfg.from_email || ""}
                  onChange={(e) =>
                    setEmailCfg((current) => ({
                      ...current,
                      from_email: e.target.value,
                    }))
                  }
                />
              </label>
              {(emailCfg.email_transport || "smtp") === "zeptomail_api" ? (
                <div className="helper-text">
                  The From address must belong to a sender domain verified in ZeptoMail.
                </div>
              ) : null}
            </div>
          </div>
          <div className="helper-text">
            Delivery settings are stored per organization and are used only when
            email delivery is enabled for that organization.
          </div>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={saveEmail}
            >
              Save email configuration
            </button>
          </div>
          </div>

          <div className="section notify-template-card">
            <div className="page-header-row">
              <div>
                <h3>Email queue settings</h3>
                <div className="helper-text">
                  Global Redis/RQ delivery controls for queued auth and workflow
                  emails.
                </div>
              </div>
            </div>
            <div className="form-grid email-config-grid">
              <label className="field span-12 checkbox-row">
                <input
                  type="checkbox"
                  checked={!!queuePolicy.email_queue_enabled}
                  onChange={(e) =>
                    setQueuePolicy((current) => ({
                      ...current,
                      email_queue_enabled: e.target.checked,
                    }))
                  }
                />
                <span>Enable email queue</span>
              </label>
              <div className="field span-3">
                <label>
                  Max retries
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={queuePolicy.email_max_retries ?? 3}
                    onChange={(e) =>
                      setQueuePolicy((current) => ({
                        ...current,
                        email_max_retries: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="field span-5">
                <label>
                  Retry intervals
                  <input
                    value={queuePolicy.email_retry_intervals || ""}
                    onChange={(e) =>
                      setQueuePolicy((current) => ({
                        ...current,
                        email_retry_intervals: e.target.value,
                      }))
                    }
                    placeholder="60,300,900"
                  />
                </label>
              </div>
              <div className="field span-2">
                <label>
                  Job timeout
                  <input
                    type="number"
                    min="1"
                    max="600"
                    value={queuePolicy.email_job_timeout_seconds ?? 60}
                    onChange={(e) =>
                      setQueuePolicy((current) => ({
                        ...current,
                        email_job_timeout_seconds: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="field span-2">
                <label>
                  Failure TTL hrs
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={queuePolicy.email_failure_ttl_hours ?? 24}
                    onChange={(e) =>
                      setQueuePolicy((current) => ({
                        ...current,
                        email_failure_ttl_hours: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
            </div>
            <div className="helper-text">
              Retry intervals are comma-separated positive seconds. If fewer
              intervals are supplied than retries, the last interval is reused.
            </div>
            {queueHealth ? (
              <div className="section" style={{ marginTop: 12 }}>
                <strong>Queue health</strong>
                <div className="helper-text">
                  Redis:{" "}
                  {queueHealth.redis_reachable ? "reachable" : "not reachable"}{" "}
                  · Queue: {queueHealth.queue_name || "email"} · Queued:{" "}
                  {queueHealth.queued_count ?? 0} · Failed:{" "}
                  {queueHealth.failed_count ?? 0} · Dead recent:{" "}
                  {queueHealth.dead_recent_count ?? 0}
                </div>
              </div>
            ) : null}
            <div className="section" style={{ marginTop: 12, overflowX: "auto" }}>
              <div className="page-header-row">
                <div>
                  <strong>Email jobs</strong>
                  <div className="helper-text">
                    Filter and page through queued email jobs. Partial/dead rows show recipient-level failure details.
                  </div>
                </div>
                <label className="field" style={{ minWidth: 220 }}>
                  <span>Status</span>
                  <select
                    value={queueJobsStatus}
                    onChange={(e) => {
                      setQueueJobsStatus(e.target.value);
                      setQueueJobsPage(1);
                    }}
                  >
                    <option value="">All statuses</option>
                    {EMAIL_JOB_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <table className="table table--dense" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Purpose</th>
                    <th>Attempts</th>
                    <th>Subject</th>
                    <th>Recipients</th>
                    <th>Created / Updated</th>
                    <th>Last error</th>
                  </tr>
                </thead>
                <tbody>
                  {queueJobs.map((job) => {
                    const failed = job.failed_recipients || {};
                    const pending = job.pending_recipients || [];
                    const sent = job.sent_recipients || [];
                    return (
                      <tr key={job.id}>
                        <td>
                          <span className={`pill ${job.status === "partial" ? "pill-warn" : job.status === "dead" || job.status === "failed" ? "pill-danger" : ""}`.trim()}>
                            {job.status || "—"}
                          </span>
                        </td>
                        <td>{job.purpose || "—"}</td>
                        <td>{job.attempts ?? 0} / {job.max_retries ?? 0}</td>
                        <td style={{ maxWidth: 260, whiteSpace: "normal", wordBreak: "break-word" }}>{job.subject || "—"}</td>
                        <td style={{ minWidth: 220 }}>
                          <div>To: {(job.to_emails || []).join(", ") || "—"}</div>
                          {(job.cc_emails || []).length ? <div>CC: {(job.cc_emails || []).join(", ")}</div> : null}
                          <div>Sent: {sent.length}</div>
                          <div>Pending: {pending.length}</div>
                          {Object.keys(failed).length ? (
                            <div className="helper-text">Failed: {Object.keys(failed).join(", ")}</div>
                          ) : null}
                        </td>
                        <td>
                          <div>{formatDateTimeFull(job.created_at)}</div>
                          <div className="helper-text">Updated: {formatDateTimeFull(job.updated_at)}</div>
                        </td>
                        <td style={{ maxWidth: 360, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {job.last_error || (Object.keys(failed).length ? JSON.stringify(failed, null, 2) : "—")}
                        </td>
                      </tr>
                    );
                  })}
                  {!queueJobs.length ? (
                    <tr>
                      <td colSpan={7}>No email jobs match the current filter.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
              <Pager
                page={queueJobsPage}
                pageCount={Math.max(1, Math.ceil((queueJobsTotal || 0) / queueJobsPageSize))}
                size={queueJobsPageSize}
                setSize={(nextSize) => {
                  setQueueJobsPageSize(nextSize);
                  setQueueJobsPage(1);
                }}
                prev={() => setQueueJobsPage((page) => Math.max(1, page - 1))}
                next={() =>
                  setQueueJobsPage((page) =>
                    Math.min(
                      Math.max(1, Math.ceil((queueJobsTotal || 0) / queueJobsPageSize)),
                      page + 1,
                    ),
                  )
                }
                canPrev={queueJobsPage > 1}
                canNext={
                  queueJobsPage <
                  Math.max(1, Math.ceil((queueJobsTotal || 0) / queueJobsPageSize))
                }
                total={queueJobsTotal || 0}
                start={(queueJobsPage - 1) * queueJobsPageSize}
              />
            </div>

            <div className="card-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={saveQueuePolicy}
              >
                Save email queue settings
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  load();
                  fetchQueueJobs(queueJobsPage, queueJobsStatus, queueJobsPageSize);
                }}
              >
                Refresh health
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <PinModal
        uid={user?.uid || ""}
        open={pinGate.pinOpen}
        onClose={pinGate.closePin}
        onVerified={pinGate.handlePinVerified}
      />
    </div>
  );
}
